import { LpDashboardReport } from '@livepolls/ui-components/src/components/dashboard-report/LpDashboardReport';
import { LpDashboardReportForMobile } from '@livepolls/ui-components/src/components/dashboard-report/LpDashboardReportForMobile';
import { LpSpinner } from '@livepolls/ui-components/src/components/spinner/LpSpinner';
import { TABLET_SCREEN_BREAKPOINT } from '@livepolls/ui-components/src/constants/device-breakpoints.contants';
import { useScreenResize } from '@livepolls/ui-components/src/utils/getScreenSize.util';
import { SHARABLE_DASHBOARD_URL } from 'src/constants/app.constants';
import { DASHBOARD_MAX_LEADERBOARD_ROWS } from 'src/constants/leaderboard-rows.constants';
import {
  fetchQuestionResult,
  useLivePoll,
  useLivePollSession,
  useLivePollSessionReport,
} from 'src/hooks/store-hooks';
import styles from './DashboardReports.module.css';
import { t } from 'i18next';
import { useDownloadReportStore } from 'src/screens/livepoll-design/store/useDownloadReport.store';
import { startDownloadableReportCreationSelector } from 'src/screens/livepoll-design/store/DownloadReportStore.selector';

interface Props {
  livePollId: number;
  livePollSessionId: number;
  onBackButtonClick(): void;
}

export const DashboardReports = ({
  livePollId,
  livePollSessionId,
  onBackButtonClick,
}: Props) => {
  const text = {
    quiz: t('quiz'),
    poll: t('poll'),
    duration: t('duration'),
    question: t('question'),
    questions: t('questions'),
    participant: t('participant'),
    participants: t('participants'),
    noVotes: t('noVotes'),
    outOf: t('outOf'),
    votes: t('votes'),
    initializing: t('initializing'),
    noDataAvailable: t('noDataAvailable'),
    default: t('default'),
    popularity: t('popularity'),
    accuracy: t('accuracy'),
    reverseOrder: t('reverseOrder'),
    sortBy: t('sortBy'),
    profileQuestion: t('profileQuestion'),
    teamRankings: t('teamRankings'),
    individualView: t('individualView'),
    teamsView: t('teamsView'),
    mentions: t('mentions'),
    popularMentions: t('popularMentions'),
    wordCloudText: t('wordCloud'),
    multipleChoice: t('multipleChoice'),
    topScorers: t('topScorers'),
    swipeQuestionCorrectText: t('swipeQuestionCorrectText'),
    swipeQuestionIncorrectText: t('swipeQuestionIncorrectText'),
    questionTypeText: t('swipeQuestionText'),
    easy: t('easy'),
    normal: t('normal'),
    hard: t('hard'),
    livefeedQuestionText: t('livefeedQuestionText'),
    liveFeed: t('allResponses'),
    topVoted: t('voted'),
    bookmarked: t('favorites'),
    comments: t('comments'),
    showing: t('showing'),
    showUpTo: t('showUpTo'),
    of: t('of'),
    to: t('to'),
    invalidDropdownError: t('invalidDropdownError'),
    noComments: t('noComments'),
    noVotesForComments: t('noVotesForComments'),
    noBookmarkedComments: t('noBookmarkedComments'),
    submissionTime: t('submissionTime'),
    name: t('name'),
    favorites: t('favorites'),
    leaderboard: t('leaderboard'),
    downloadCsv: t('downloadCsv'),
  };
  const { width } = useScreenResize();
  const startDownloadableReportCreation = useDownloadReportStore(
    startDownloadableReportCreationSelector,
  );

  const {
    isLoading: isLivePollLoading,
    error: DashboardError,
    data: livePoll,
  } = useLivePoll(livePollId);
  const {
    isLoading: isLivePollSessionReportLoading,
    error: livePollSessionReportFetchError,
    data: livePollSessionReport,
  } = useLivePollSessionReport(
    livePollId,
    livePollSessionId,
    DASHBOARD_MAX_LEADERBOARD_ROWS,
  );

  const {
    isLoading: isLivePollSessionLoading,
    error: livePollSessionFetchError,
    data: livePollSession,
  } = useLivePollSession(livePollId, livePollSessionId);
  if (DashboardError) {
    throw DashboardError;
  }
  if (livePollSessionFetchError) {
    throw livePollSessionFetchError;
  }

  if (livePollSessionReportFetchError) {
    throw livePollSessionReportFetchError;
  }

  if (isLivePollLoading) {
    return (
      <div className={styles.loaderContainer}>
        <LpSpinner message="Loading LivePoll..." />
      </div>
    );
  }

  if (isLivePollSessionLoading) {
    return (
      <div className={styles.loaderContainer}>
        <LpSpinner message="Loading LivePoll Session..." />
      </div>
    );
  }
  if (isLivePollSessionReportLoading) {
    return (
      <div className={styles.loaderContainer}>
        <LpSpinner message="Loading Session Report..." />
      </div>
    );
  }

  if (!livePoll) {
    return <div>Oops an error occurred (LivePoll not found).</div>;
  }

  if (!livePollSession) {
    return <div>Oops an error occurred (LivePollSession not found).</div>;
  }

  if (!livePollSessionReport) {
    return <div>Oops an error occurred (LivePollSessionReport not found).</div>;
  }

  const teams = livePollSession.setting?.teamSetting?.teams || [];
  const isTabletView = width <= TABLET_SCREEN_BREAKPOINT;
  const sharableReportDashboardUid = livePollSession.sharableReportDashboardUid;

  const fetchLiveFeedQuestionResult = async (questionId: string) => {
    return await fetchQuestionResult(livePollId, livePollSessionId, questionId);
  };

  const downloadRawSessionReport = async (livePollSessionId: number) => {
    return startDownloadableReportCreation(livePollSessionId);
  };

  const reportUrl = getReportUrl(sharableReportDashboardUid);
  if (isTabletView) {
    return (
      <LpDashboardReportForMobile
        livePollSession={livePollSession}
        livePoll={livePoll}
        livePollSessionReport={livePollSessionReport}
        reportUrl={reportUrl}
        teams={teams}
        fetchLiveFeedQuestionResult={questionId =>
          fetchLiveFeedQuestionResult(questionId)
        }
        downloadRawSessionReport={livePollSessionId =>
          downloadRawSessionReport(livePollSessionId)
        }
        text={text}
      />
    );
  }

  return (
    <LpDashboardReport
      livePoll={livePoll}
      livePollSession={livePollSession}
      onBackButtonClick={onBackButtonClick}
      livePollSessionReport={livePollSessionReport}
      reportUrl={reportUrl}
      fetchLiveFeedQuestionResult={questionId =>
        fetchLiveFeedQuestionResult(questionId)
      }
      downloadRawSessionReport={sharableReportDashboardUid =>
        downloadRawSessionReport(sharableReportDashboardUid)
      }
      text={text}
    />
  );
};

const getReportUrl = (reportUid?: string) => {
  if (reportUid != null) {
    return `${SHARABLE_DASHBOARD_URL}/${reportUid}`;
  }
};
